import { DateInput, Select, TextInput } from "risa-oasis-ui_v2";

import { CallingFormData } from "../../../sidePanel/submissionSummarySidePanel/utils/buildCallingFormDataFromOrder";
import { SelectOption } from "../../../sidePanel/submissionSummarySidePanel/utils/resolveSelectOptionValue";
import {
  CallSummaryMode,
  CALL_SUMMARY_MODE_LABELS,
} from "../utils/callSummaryMode";
import FaxDocumentUpload from "./faxDocumentUpload";

interface CallSummaryFieldsProps {
  mode: CallSummaryMode;
  formData: CallingFormData;
  /** Read-only timestamp captured when the modal was opened. */
  dateTime: string;
  /**
   * Overrides the read-only Second STP Outcome label. When set (e.g. "Appeal"
   * from Case Appeals), the field no longer mirrors the Call/Fax tab.
   */
  secondStpOutcomeLabel?: string;
  pocOptions: SelectOption[];
  responseStatusOptions: SelectOption[];
  caseStatusOptions: SelectOption[];
  /** Document attached on the Fax tab, or null when none is selected. */
  faxDocument: File | null;
  onFaxDocumentChange: (file: File | null) => void;
  isSaving: boolean;
  onChange: (field: keyof CallingFormData, value: string) => void;
}

/**
 * Lifts the oasis field labels one step above the library default, per the
 * design. `label` is the text/date input label element, `.label` the select's.
 */
const FIELD_LABEL_CLASSES = [
  "[&_label]:!text-small [&_label]:!leading-small [&_label]:!text-primaryGray-3",
  "[&_.label]:!text-small [&_.label]:!leading-small [&_.label]:!text-primaryGray-3",
].join(" ");

/** Mutes the value text of the two read-only fields. */
const READ_ONLY_FIELD_CLASSES = "[&_input[readonly]]:!text-primaryGray-6";

/**
 * Comment box: an oasis text input's look — 1px #d6d6d6 border, 4px radius,
 * tiny placeholder — as a textarea, so long comments wrap onto new lines
 * instead of scrolling out of view. `font-[inherit]` because a textarea does
 * not inherit the page font on its own.
 */
const COMMENT_INPUT_CLASSES = [
  "h-20 w-full resize-none rounded border border-solid border-primaryGray-14",
  "px-3 py-2 font-[inherit] text-small text-primaryGray-1 outline-none",
  "placeholder:text-tiny placeholder:text-primaryGray-7",
  "focus:border-primaryGray-6",
].join(" ");

/**
 * Returns the stored value only when it exists in the option list, so the
 * oasis Select falls back to its placeholder for unknown values.
 *
 * @input options - Options rendered by the select
 * @input currentValue - Value held in form state
 * @return Value safe to pass as the select default
 */
const getSelectDefaultValue = (
  options: SelectOption[],
  currentValue: string,
): string => {
  if (!currentValue) {
    return "";
  }

  return options.some((option) => option.value === currentValue)
    ? currentValue
    : "";
};

/**
 * Two-column field grid for the Call and Fax summary forms. The fax variant
 * swaps the phone number for a fax number, the insurance rep for the payer or
 * department the fax was sent to, and adds the Attach Document control. Second
 * STP Outcome is read-only and shows Call or Fax from the open tab,
 * unless `secondStpOutcomeLabel` overrides it (e.g. "Appeal" on Case Appeals).
 *
 * @param props - Field values, option lists and the change handler
 */
const CallSummaryFields = (props: CallSummaryFieldsProps) => {
  const { formData, mode } = props;
  const isFaxMode = mode === CallSummaryMode.FAX;

  return (
    <div className={`grid grid-cols-2 gap-x-6 gap-y-5 ${FIELD_LABEL_CLASSES}`}>
      <div className={READ_ONLY_FIELD_CLASSES}>
        <TextInput
          id="appeal-summary-date-time"
          label="Date and Time"
          defaultValue={props.dateTime}
          readOnly
        />
      </div>

      {isFaxMode ? (
        <TextInput
          id="appeal-summary-fax-number"
          label="Fax Number"
          placeholder="Enter Fax Number"
          defaultValue={formData.faxNumber}
          onChange={(event) => props.onChange("faxNumber", event.value)}
          required
        />
      ) : (
        <TextInput
          id="appeal-summary-phone-number"
          label="Phone Number"
          placeholder="Enter Phone Number"
          defaultValue={formData.phoneNumber}
          onChange={(event) => props.onChange("phoneNumber", event.value)}
          required
        />
      )}

      <TextInput
        id="appeal-summary-case-id"
        label="Case ID"
        placeholder="Enter Case ID"
        defaultValue={formData.caseId}
        onChange={(event) => props.onChange("caseId", event.value)}
        required
      />

      {isFaxMode ? (
        <TextInput
          id="appeal-summary-sent-to"
          label="Sent To (Payer / Dept)"
          placeholder="Enter Payer/Dept Name"
          defaultValue={formData.sentTo}
          onChange={(event) => props.onChange("sentTo", event.value)}
          required
        />
      ) : (
        <TextInput
          id="appeal-summary-rep-name"
          label="Insurance Rep"
          placeholder="Enter Rep Name"
          defaultValue={formData.repName}
          onChange={(event) => props.onChange("repName", event.value)}
          required
        />
      )}

      <div className={READ_ONLY_FIELD_CLASSES}>
        <TextInput
          id="appeal-summary-second-stp-outcome"
          label="Second STP Outcome"
          defaultValue={
            props.secondStpOutcomeLabel ?? CALL_SUMMARY_MODE_LABELS[mode]
          }
          readOnly
        />
      </div>

      <Select
        id="appeal-summary-response-status"
        label="Response Status"
        options={props.responseStatusOptions}
        placeholder="Select"
        defaultValue={getSelectDefaultValue(
          props.responseStatusOptions,
          formData.responseStatus,
        )}
        onOptionChange={(option) =>
          props.onChange("responseStatus", option.value)
        }
        required
      />

      {/*
        Comment is a plain textarea rather than the oasis input, which is
        single-line and hard-sets `height: 2.5rem !important`. The styles below
        mirror an oasis text input at a taller, wrapping height.
      */}
      <div className="col-span-2 flex flex-col gap-1">
        <label htmlFor="appeal-summary-comment">
          Comment
          <span className="text-[1rem] text-[red]"> *</span>
        </label>
        <textarea
          id="appeal-summary-comment"
          name="appeal-summary-comment"
          rows={3}
          placeholder="Enter Comment"
          defaultValue={formData.comment}
          onChange={(event) => props.onChange("comment", event.target.value)}
          className={COMMENT_INPUT_CLASSES}
        />
      </div>

      <Select
        id="appeal-summary-poc"
        label="POC"
        options={props.pocOptions}
        placeholder="Select"
        defaultValue={getSelectDefaultValue(
          props.pocOptions,
          formData.callingPoc,
        )}
        onOptionChange={(option) => props.onChange("callingPoc", option.value)}
        required
      />

      <Select
        id="appeal-summary-case-status"
        label="Case Status"
        options={props.caseStatusOptions}
        placeholder="Select"
        defaultValue={getSelectDefaultValue(
          props.caseStatusOptions,
          formData.caseStatus,
        )}
        onOptionChange={(option) => props.onChange("caseStatus", option.value)}
        required
      />

      {isFaxMode && (
        <div className="col-span-2">
          <FaxDocumentUpload
            file={props.faxDocument}
            onFileChange={props.onFaxDocumentChange}
            disabled={props.isSaving}
          />
        </div>
      )}

      <TextInput
        id="appeal-summary-next-action"
        label="Next Action"
        placeholder="Enter Next Action"
        defaultValue={formData.nextAction}
        onChange={(event) => props.onChange("nextAction", event.value)}
      />

      <DateInput
        id="appeal-summary-follow-up-date"
        label="Follow Up Date"
        format="MM/DD/YYYY"
        defaultValue={formData.followUpDate}
        onChange={(event) => props.onChange("followUpDate", event.value)}
      />
    </div>
  );
};

export default CallSummaryFields;
